// Compositor de una comunicación: contenido a la izquierda, "A quién va" a la derecha.
//
// Flujo: redactar → elegir público (conteo en vivo) → vista previa / envío de prueba
// → "Enviar a N". Al enviar se guarda, y se encola con la HUELLA del público que se
// mostró: si el filtro cambió entre medio, el servidor lo rechaza.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Card, Input, Modal, Radio, Select, Spin } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, SendOutlined, ExperimentOutlined, SaveOutlined } from '@ant-design/icons';
import Comunicaciones, { mensajeError } from '../../../../service/Comunicaciones';
import { EncabezadoAdmin, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import EditorBloques, { aBloquesApi, bloqueNuevo, deBloquesApi } from './EditorBloques';
import { ResumenPublico, SelectorPublico, segmentoVacio } from './SelectorPublico';
import VistaPreviaCorreo from './VistaPreviaCorreo';
import { MOTIVOS_DEFECTO, PLANTILLAS } from './textos';

const Campo = ({ label, children, requerido }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1">
      {label}{requerido && <span className="text-red-500"> *</span>}
    </span>
    {children}
  </label>
);

const segmentoCompleto = (s) => !!s?.tipo && !(s.horario && !s.taller);

/** Validación local, para avisar antes de ir al servidor. El servidor revalida todo. */
const problemas = ({ asunto, plantilla, datos, bloques }) => {
  const p = [];
  if ((asunto || '').trim().length < 3) p.push('Escribe un asunto (mínimo 3 caracteres).');
  if (aBloquesApi(bloques).length === 0) p.push('El correo no tiene contenido.');
  if (plantilla === 'operativa_taller') {
    if (!datos.motivo) p.push('Elige el motivo del aviso.');
    if (!datos.actividad?.trim()) p.push('Indica el taller al que se refiere.');
    if (datos.motivo === 'cambio_horario' && !datos.horarioNuevo?.trim()) p.push('Indica el horario nuevo.');
    if (datos.motivo === 'cambio_lugar' && !datos.lugarNuevo?.trim()) p.push('Indica el lugar nuevo.');
    if (datos.motivo === 'suspension' && !datos.fechaAfectada?.trim()) p.push('Indica qué día se suspende.');
  }
  return p;
};

export default function ComunicacionNueva() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const desde = params.get('desde');
  const navigate = useNavigate();
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();

  const [opciones, setOpciones] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [idGuardado, setIdGuardado] = useState(id || null);

  const [plantilla, setPlantilla] = useState('aviso_general');
  const [datos, setDatos] = useState({});
  const [asunto, setAsunto] = useState('');
  const [bloques, setBloques] = useState(() => [bloqueNuevo('parrafo')]);
  const [segmento, setSegmento] = useState(segmentoVacio('inscritos'));

  const [conteo, setConteo] = useState(null);
  const [contando, setContando] = useState(false);
  const [errorConteo, setErrorConteo] = useState(null);

  const [intentado, setIntentado] = useState(false);
  const [ocupado, setOcupado] = useState(null); // 'guardar' | 'previa' | 'prueba' | 'enviar'
  const [previa, setPrevia] = useState(null);
  const [pruebaAbierta, setPruebaAbierta] = useState(false);
  const [correoPrueba, setCorreoPrueba] = useState('');

  // ── Carga inicial ──
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const op = await Comunicaciones.opciones();
        if (!vivo) return;
        setOpciones(op);
        const origen = id || desde;
        if (origen) {
          const c = await Comunicaciones.obtener(origen);
          if (!vivo) return;
          if (id && c.estado !== 'borrador') {
            navigate(`/admin/comunicaciones/${id}`, { replace: true });
            return;
          }
          setPlantilla(c.plantilla || 'aviso_general');
          setDatos(c.datos || {});
          setAsunto(c.asunto || '');
          setBloques(c.bloques?.length ? deBloquesApi(c.bloques) : [bloqueNuevo('parrafo')]);
          setSegmento(c.segmento || segmentoVacio('inscritos'));
        } else if (!op.alcance?.inscritos) {
          const primero = ['matriculados', 'clientes'].find((t) => op.alcance?.[t]);
          if (primero) setSegmento(segmentoVacio(primero));
        }
      } catch (e) {
        notify.error(mensajeError(e, 'No se pudo cargar el compositor'));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, desde]);

  useEffect(() => {
    if (!correoPrueba && user?.email) setCorreoPrueba(user.email);
  }, [user, correoPrueba]);

  // ── Conteo en vivo del público (con debounce y cancelando el anterior) ──
  const claveSegmento = JSON.stringify(segmento);
  const abortRef = useRef(null);
  useEffect(() => {
    if (cargando || !segmentoCompleto(segmento)) return undefined;
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setContando(true);
      setErrorConteo(null);
      try {
        const r = await Comunicaciones.publico(segmento, { signal: ctrl.signal });
        if (!ctrl.signal.aborted) setConteo(r);
      } catch (e) {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setConteo(null);
        setErrorConteo(mensajeError(e, 'No se pudo contar el público'));
      } finally {
        if (!ctrl.signal.aborted) setContando(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveSegmento, cargando]);

  const payload = () => ({ asunto: asunto.trim(), plantilla, datos, bloques: aBloquesApi(bloques), segmento });
  const listaProblemas = useMemo(
    () => problemas({ asunto, plantilla, datos, bloques }),
    [asunto, plantilla, datos, bloques],
  );

  const guardar = async () => {
    const body = payload();
    const c = idGuardado ? await Comunicaciones.actualizar(idGuardado, body) : await Comunicaciones.crear(body);
    setIdGuardado(c._id);
    return c;
  };

  const exigirValido = () => {
    setIntentado(true);
    if (listaProblemas.length) {
      notify.warning(listaProblemas[0]);
      return false;
    }
    return true;
  };

  const onGuardarBorrador = async () => {
    if ((asunto || '').trim().length < 3) {
      setIntentado(true);
      notify.warning('Escribe un asunto para guardar el borrador.');
      return;
    }
    setOcupado('guardar');
    try {
      await guardar();
      notify.success('Borrador guardado');
      navigate('/admin/comunicaciones');
    } catch (e) {
      notify.error(mensajeError(e, 'No se pudo guardar'));
    } finally {
      setOcupado(null);
    }
  };

  const onVistaPrevia = async () => {
    if (!exigirValido()) return;
    setOcupado('previa');
    try {
      setPrevia(await Comunicaciones.previsualizar(payload()));
    } catch (e) {
      notify.error(mensajeError(e, 'No se pudo armar la vista previa'));
    } finally {
      setOcupado(null);
    }
  };

  const onEnviarPrueba = async () => {
    setOcupado('prueba');
    try {
      const c = await guardar();
      const r = await Comunicaciones.prueba(c._id, correoPrueba.trim());
      notify.success(`Prueba enviada a ${r.para}`);
      setPruebaAbierta(false);
    } catch (e) {
      notify.error(mensajeError(e, 'No se pudo enviar la prueba'));
    } finally {
      setOcupado(null);
    }
  };

  const onEnviar = () => {
    if (!exigirValido()) return;
    if (!conteo?.total) return;
    const n = conteo.total.toLocaleString('es-CL');
    modal.confirm({
      title: `¿Enviar a ${n} ${conteo.total === 1 ? 'persona' : 'personas'}?`,
      content: (
        <div className="text-sm text-gray-600 space-y-1">
          <p className="m-0"><strong>{asunto.trim()}</strong></p>
          <p className="m-0">{conteo.descripcion}</p>
          <p className="m-0">El envío sale en lotes durante los próximos minutos. Se puede detener, pero lo que ya salió no se recupera.</p>
        </div>
      ),
      okText: `Enviar a ${n}`,
      cancelText: 'Revisar',
      onOk: async () => {
        setOcupado('enviar');
        try {
          const c = await guardar();
          const r = await Comunicaciones.encolar(c._id, conteo.huella);
          notify.success(`Envío en curso a ${r.totalDestinatarios.toLocaleString('es-CL')} personas`);
          navigate(`/admin/comunicaciones/${c._id}`);
        } catch (e) {
          notify.error(mensajeError(e, 'No se pudo iniciar el envío'));
          if (e?.response?.data?.code === 'publico_cambiado') setSegmento({ ...segmento });
        } finally {
          setOcupado(null);
        }
      },
    });
  };

  if (cargando) return <div className="p-10 flex justify-center"><Spin /></div>;

  const motivos = opciones?.motivosOperativa || MOTIVOS_DEFECTO;
  const setDato = (k) => (e) => setDatos({ ...datos, [k]: e?.target ? e.target.value : e });

  return (
    <div className="p-4 md:p-6">
      <button type="button" onClick={() => navigate('/admin/comunicaciones')} className="text-sm text-gray-500 hover:text-primary mb-2 inline-flex items-center gap-1">
        <ArrowLeftOutlined /> Comunicaciones
      </button>
      <EncabezadoAdmin
        titulo={id ? 'Editar' : 'Nuevo'}
        destacado="correo"
        subtitulo={desde ? 'Duplicado de una comunicación anterior. Revisa el público antes de enviar.' : 'Redacta el aviso y elige a quién va.'}
      />

      {opciones?.correoDesviadoA && (
        <Alert className="mb-4" type="warning" showIcon
          message={`Entorno de pruebas: todos los correos se desvían a ${opciones.correoDesviadoA}.`}
          description={`Este envío NO llegará a las personas. Con el desvío activo solo se permiten envíos de hasta ${opciones.limites?.publicoConDesvio ?? 5} destinatarios.`} />
      )}
      {opciones?.proveedor === 'consola' && (
        <Alert className="mb-4" type="info" showIcon message="Modo desarrollo: los correos no salen, se guardan como HTML en var/correos del backend." />
      )}
      {opciones && !opciones.bajaConfigurada && (
        <Alert className="mb-4" type="error" showIcon message="Falta configurar MAIL_BAJA_SECRET en el backend: sin enlace de baja no se pueden enviar correos masivos." />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <Card size="small" title="Tipo de correo">
            <Radio.Group value={plantilla} onChange={(e) => { setPlantilla(e.target.value); setDatos({}); }} className="flex flex-col gap-2">
              {Object.entries(PLANTILLAS).map(([k, p]) => (
                <Radio key={k} value={k}>
                  <span className="font-semibold">{p.label}</span>
                  <span className="block text-xs text-gray-500">{p.descripcion}</span>
                </Radio>
              ))}
            </Radio.Group>
          </Card>

          {plantilla === 'operativa_taller' && (
            <Card size="small" title="Datos del aviso">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Campo label="Motivo" requerido>
                  <Select className="w-full" placeholder="Elige" value={datos.motivo} onChange={setDato('motivo')}
                    options={motivos.map((m) => ({ value: m.key, label: m.label }))} />
                </Campo>
                <Campo label="Taller" requerido>
                  <Input value={datos.actividad} maxLength={120} onChange={setDato('actividad')} placeholder="Ej. Yoga adulto · Estadio Nacional" />
                </Campo>
                {datos.motivo === 'cambio_horario' && (
                  <>
                    <Campo label="Horario nuevo" requerido>
                      <Input value={datos.horarioNuevo} maxLength={120} onChange={setDato('horarioNuevo')} placeholder="Ej. Martes y jueves 19:00" />
                    </Campo>
                    <Campo label="Horario anterior">
                      <Input value={datos.horarioAnterior} maxLength={120} onChange={setDato('horarioAnterior')} />
                    </Campo>
                  </>
                )}
                {datos.motivo === 'cambio_lugar' && (
                  <Campo label="Lugar nuevo" requerido>
                    <Input value={datos.lugarNuevo} maxLength={120} onChange={setDato('lugarNuevo')} />
                  </Campo>
                )}
                {(datos.motivo === 'suspension' || datos.motivo === 'recordatorio') && (
                  <Campo label={datos.motivo === 'suspension' ? 'Día sin clase' : 'Fecha'} requerido={datos.motivo === 'suspension'}>
                    <Input value={datos.fechaAfectada} maxLength={120} onChange={setDato('fechaAfectada')} placeholder="Ej. Lunes 20 de octubre" />
                  </Campo>
                )}
              </div>
            </Card>
          )}

          <Card size="small" title="Contenido">
            <div className="space-y-3">
              <Campo label="Asunto" requerido>
                <Input value={asunto} maxLength={opciones?.limites?.asunto ?? 150} showCount onChange={(e) => setAsunto(e.target.value)}
                  placeholder="Ej. Cambio de horario en Yoga adulto" status={intentado && asunto.trim().length < 3 ? 'error' : undefined} />
              </Campo>
              <EditorBloques bloques={bloques} onChange={setBloques} limite={opciones?.limites?.bloques ?? 30} />
            </div>
          </Card>

          {intentado && listaProblemas.length > 0 && (
            <Alert type="error" showIcon message="Falta completar" description={<ul className="m-0 pl-4">{listaProblemas.map((p) => <li key={p}>{p}</li>)}</ul>} />
          )}
        </div>

        <div className="space-y-4 lg:sticky lg:top-4">
          <Card size="small" title="A quién va">
            <SelectorPublico segmento={segmento} onChange={setSegmento} opciones={opciones} />
            <div className="border-t border-gray-100 mt-4 pt-4">
              <ResumenPublico conteo={conteo} cargando={contando} error={errorConteo} />
            </div>
          </Card>

          <Card size="small">
            <div className="flex flex-col gap-2">
              <button type="button" className={`${claseBtnPrimario} justify-center`} onClick={onEnviar}
                disabled={!conteo?.total || contando || !!errorConteo || !!ocupado || !opciones?.bajaConfigurada}>
                <SendOutlined /> {conteo?.total ? `Enviar a ${conteo.total.toLocaleString('es-CL')}` : 'Enviar'}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className={`${claseBtnSecundario} justify-center`} onClick={onVistaPrevia} disabled={!!ocupado}>
                  <EyeOutlined /> Vista previa
                </button>
                <button type="button" className={`${claseBtnSecundario} justify-center`} onClick={() => exigirValido() && setPruebaAbierta(true)} disabled={!!ocupado}>
                  <ExperimentOutlined /> Prueba
                </button>
              </div>
              <Button type="text" icon={<SaveOutlined />} onClick={onGuardarBorrador} loading={ocupado === 'guardar'} disabled={!!ocupado && ocupado !== 'guardar'}>
                Guardar borrador
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <VistaPreviaCorreo abierta={!!previa} onCerrar={() => setPrevia(null)} asunto={previa?.asunto} html={previa?.html} />

      <Modal
        open={pruebaAbierta}
        title="Enviar una prueba"
        okText="Enviar prueba"
        cancelText="Cancelar"
        onCancel={() => setPruebaAbierta(false)}
        onOk={onEnviarPrueba}
        confirmLoading={ocupado === 'prueba'}
        okButtonProps={{ disabled: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoPrueba.trim()) }}
      >
        <p className="text-sm text-gray-600">Llega un solo correo con el asunto marcado como [PRUEBA]. No se manda al público y el borrador queda guardado.</p>
        <Input type="email" value={correoPrueba} onChange={(e) => setCorreoPrueba(e.target.value)} placeholder="tu@correo.cl" onPressEnter={onEnviarPrueba} />
      </Modal>
    </div>
  );
}
